import React, { useMemo } from 'react';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';
import { FeedbacksReportsResponse } from '../../types/reports';
import { format, parseISO } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { processFeedbacksToWeekly } from '../../utils/feedbackReportUtils';

interface SessionFeedbackModalProps {
  data: FeedbacksReportsResponse;
  comparisonData?: FeedbacksReportsResponse | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ChartDataPoint {
  weekLabel: string;
  weekStartDate: string;
  [key: string]: string | number;
}

const COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // emerald-500  
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#06B6D4', // cyan-500
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="font-semibold text-gray-900 mb-2">Week of {label}</p>
      {payload
        .filter((entry: any) => entry.value > 0)
        .sort((a: any, b: any) => b.value - a.value)
        .map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between gap-4 text-sm" style={{ color: entry.color }}>
            <span>{entry.name}:</span>
            <span className="font-semibold">{entry.value.toFixed(1)}</span>
          </div>
        ))}
    </div>
  );
};

export const SessionFeedbackModal: React.FC<SessionFeedbackModalProps> = ({
  data,
  comparisonData,
  isOpen,
  onClose
}) => {
  // Process feedback data - must be before early return
  const { sessionWeeks } = useMemo(() => {
    return processFeedbacksToWeekly(data);
  }, [data]);

  const chartData = useMemo(() => {
    if (!sessionWeeks || sessionWeeks.length === 0) return [];

    return sessionWeeks.map(week => {
      const weekStart = parseISO(week.weekStartDate);
      const weekLabel = format(weekStart, 'MMM d');
      
      const dataPoint: ChartDataPoint = {
        weekLabel,
        weekStartDate: week.weekStartDate
      };

      // Add average ratings for each feedback metric
      week.sessionFeedbacks.forEach(stat => {
        dataPoint[stat.key] = Math.round(stat.average * 100) / 100;
      });

      return dataPoint;
    });
  }, [sessionWeeks]);

  // Get all unique feedback keys
  const feedbackKeys = useMemo(() => {
    const keys = new Set<string>();
    sessionWeeks.forEach(week => {
      week.sessionFeedbacks.forEach(stat => {
        keys.add(stat.key);
      });
    });
    return Array.from(keys);
  }, [sessionWeeks]);

  // Calculate statistics
  const stats = useMemo(() => {
    const sessionFeedbacks = data.feedbacks.filter(f => f.type === 'session');
    const ratings = sessionFeedbacks
      .filter(f => f.feedbackData.overallRating != null)
      .map(f => f.feedbackData.overallRating!);
    
    const avgRating = ratings.length > 0
      ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length
      : null;

    // Comparison stats
    let comparisonAvgRating = null;
    let trend = 0;
    let trendType: 'up' | 'down' | 'stable' = 'stable';

    if (comparisonData) {
      const comparisonSessionFeedbacks = comparisonData.feedbacks.filter(f => f.type === 'session');
      const comparisonRatings = comparisonSessionFeedbacks
        .filter(f => f.feedbackData.overallRating != null)
        .map(f => f.feedbackData.overallRating!);
      
      if (comparisonRatings.length > 0) {
        comparisonAvgRating = comparisonRatings.reduce((sum, r) => sum + r, 0) / comparisonRatings.length;
        
        if (avgRating !== null) {
          trend = avgRating - comparisonAvgRating;
          trendType = trend > 0 ? 'up' : trend < 0 ? 'down' : 'stable';
        }
      }
    }

    return {
      avgRating,
      totalFeedbacks: sessionFeedbacks.length,
      uniqueSessions: data.summary.statistics.uniqueSessions,
      uniqueUsers: data.summary.statistics.uniqueUsers,
      comparisonAvgRating,
      trend,
      trendType
    };
  }, [data, comparisonData]);

  // Get label for a feedback key
  const getKeyLabel = (key: string): string => {
    const week = sessionWeeks.find(w => w.sessionFeedbacks.some(s => s.key === key));
    const stat = week?.sessionFeedbacks.find(s => s.key === key);
    return stat?.label || key;
  };

  if (!isOpen) return null;

  const TrendIcon = stats.trendType === 'up' 
    ? TrendingUp 
    : stats.trendType === 'down' 
      ? TrendingDown 
      : Minus;

  const trendColor = stats.trendType === 'up' 
    ? 'text-green-600' 
    : stats.trendType === 'down' 
      ? 'text-red-600' 
      : 'text-rm-neutral-600';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-card shadow-card max-w-5xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-rm-neutral-600/10">
            <div>
              <h2 className="text-xl font-semibold text-rm-primary-700">
                Session Satisfaction Rate
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-2xl font-bold text-rm-ink-900">
                  {stats.avgRating !== null ? stats.avgRating.toFixed(1) : '—'}
                </span>
                <span className="text-sm text-rm-neutral-600">/ 10</span>
                {stats.trend !== 0 && Math.abs(stats.trend) >= 0.01 && (
                  <div className={cn('flex items-center gap-1', trendColor)}>
                    <TrendIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {stats.trend > 0 ? '+' : ''}{stats.trend.toFixed(1)} pts
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm text-rm-neutral-600 mt-1">
                {stats.totalFeedbacks} feedback{stats.totalFeedbacks !== 1 ? 's' : ''} · {stats.uniqueSessions} sessions · {stats.uniqueUsers} users
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-rm-bg-50 text-rm-neutral-600 hover:text-rm-ink-900 transition-colors self-start ml-auto"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-120px)]">
            {chartData.length > 0 ? (
              <div>
                <h3 className="text-sm font-semibold text-rm-ink-900 mb-4">
                  Session Feedback Ratings by Week ({format(parseISO(data.summary.dateRange.startDate), 'MMM d')} – {format(parseISO(data.summary.dateRange.endDate), 'MMM d, yyyy')})
                </h3>
                
                {/* Stats Summary */}
                <div className="grid grid-cols-2 gap-3 mb-6 md:grid-cols-4">
                  <div className="bg-rm-bg-50 rounded-lg px-3 py-2 border border-rm-neutral-600/10 flex items-center justify-between gap-2">
                    <p className="text-sm text-rm-neutral-600">Total Weeks</p>
                    <p className="text-sm font-semibold text-rm-ink-900">{sessionWeeks.length}</p>
                  </div>
                  <div className="bg-rm-bg-50 rounded-lg px-3 py-2 border border-rm-neutral-600/10 flex items-center justify-between gap-2">
                    <p className="text-sm text-rm-neutral-600">Metrics Tracked</p>
                    <p className="text-sm font-semibold text-rm-ink-900">{feedbackKeys.length}</p>
                  </div>
                  <div className="bg-rm-bg-50 rounded-lg px-3 py-2 border border-rm-neutral-600/10 flex items-center justify-between gap-2">
                    <p className="text-sm text-rm-neutral-600">Unique Sessions</p>
                    <p className="text-sm font-semibold text-rm-ink-900">{stats.uniqueSessions}</p>
                  </div>
                  <div className="bg-rm-bg-50 rounded-lg px-3 py-2 border border-rm-neutral-600/10 flex items-center justify-between gap-2">
                    <p className="text-sm text-rm-neutral-600">Total Feedback</p>
                    <p className="text-sm font-semibold text-rm-ink-900">{stats.totalFeedbacks}</p>
                  </div>
                </div>

                {/* Chart */}
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="weekLabel" 
                      stroke="#6B7280"
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#6B7280"
                      fontSize={12}
                      domain={[1, 10]}
                      tickCount={10}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="rect"
                      fontSize={12}
                    />
                    
                    {/* Reference line for average */}
                    {stats.avgRating !== null && (
                      <ReferenceLine 
                        y={stats.avgRating} 
                        stroke="#f59e0b" 
                        strokeDasharray="2 2"
                        label={{ value: `Avg: ${stats.avgRating.toFixed(1)}`, position: 'left', fontSize: 12 }}
                      />
                    )}
                    
                    {feedbackKeys.map((key, index) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        name={getKeyLabel(key)}
                        fill={COLORS[index % COLORS.length]}
                        radius={[2, 2, 0, 0]}
                        opacity={0.8}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No session feedback data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionFeedbackModal;
